import type {z} from 'zod';
import {SERVER_DATA_ELEMENT_ID} from '@packages/systems/server-data/shared';

export {SERVER_DATA_ELEMENT_ID} from '@packages/systems/server-data/shared';

/**
 * Read and validate server-embedded data from the DOM.
 *
 * Looks for a `<script type="application/json" id="wf-server-data">` element
 * (or a custom `elementId`), parses its text content as JSON, and validates
 * it against the provided Zod schema.
 *
 * Returns a fully typed object matching the schema, with runtime validation
 * that catches shape mismatches at startup rather than deep in application code.
 *
 * When called without a fallback, throws if the element is missing.
 * When called with a fallback, returns the fallback value if the element is
 * missing — the return type is a union of the schema output and the fallback
 * type, so the caller sees exactly what they might receive.
 */
export function readServerData<T extends z.ZodType>(
  schema: T,
  options?: {elementId?: string}
): z.output<T>;
export function readServerData<T extends z.ZodType, F>(
  schema: T,
  options: {elementId?: string; fallback: F}
): z.output<T> | F;
export function readServerData<T extends z.ZodType, F>(
  schema: T,
  options?: {elementId?: string; fallback?: F}
): z.output<T> | F {
  const id = options?.elementId ?? SERVER_DATA_ELEMENT_ID;
  const el = document.getElementById(id);

  if (!el?.textContent) {
    if (options && 'fallback' in options) return options.fallback as F;
    throw new Error(`Server data element #${id} not found or empty`);
  }

  const raw: unknown = JSON.parse(el.textContent);
  return schema.parse(raw);
}
